import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { BarChart2 } from 'lucide-react'
import { useApi } from '../hooks/useApi'
import { AnalyticsFilters, type AnalyticsFilterState } from '../components/analytics/AnalyticsFilters'
import { GeoFilter, type GeoSelection } from '../components/analytics/GeoFilter'
import { AnalyticsKPIs } from '../components/analytics/AnalyticsKPIs'
import { TrafficTrendChart } from '../components/analytics/TrafficTrendChart'
import { ChannelDistributionChart } from '../components/analytics/ChannelDistributionChart'
import { ChannelTrendsChart } from '../components/analytics/ChannelTrendsChart'
import { TrafficJourney } from '../components/analytics/TrafficJourney'
import { TopPagesTable } from '../components/analytics/TopPagesTable'
import { GeoDistribution } from '../components/analytics/GeoDistribution'

interface Client {
  id: number
  name: string
  domain: string
}

interface ConnectionEntry {
  service: string
  status: string
  config?: Record<string, unknown>
}

export default function Analytics() {
  const { projectId } = useParams<{ projectId: string }>()
  const navigate = useNavigate()

  const [filters, setFilters] = useState<AnalyticsFilterState>({
    period: 30,
    granularity: 'day',
    device: 'all',
  })

  const [geoSelection, setGeoSelection] = useState<GeoSelection | null>(null)

  const { data: client, loading: clientLoading, error: clientError } = useApi<Client>(
    `/api/clients/${projectId}`
  )

  // GA4 « configuré » = connexion pool ga4 portant un propertyId (source de vérité pool-only,
  // remplace l'ancien champ legacy Client.gaPropertyId). Aligné sur le gate backend hasGa4Connection.
  const { data: connections, loading: connectionsLoading } = useApi<ConnectionEntry[]>(
    `/api/data-sources/clients/${projectId}/connections`
  )
  const ga4Connected =
    Array.isArray(connections) &&
    !!(connections.find((c) => c.service === 'ga4')?.config as Record<string, unknown> | undefined)
      ?.propertyId

  // Build query params
  const queryParams = new URLSearchParams()
  queryParams.set('granularity', filters.granularity)
  queryParams.set('device', filters.device)

  if (filters.startDate && filters.endDate) {
    queryParams.set('startDate', filters.startDate)
    queryParams.set('endDate', filters.endDate)
  } else {
    queryParams.set('period', String(filters.period || 30))
  }

  if (filters.compareStartDate && filters.compareEndDate) {
    queryParams.set('compareStartDate', filters.compareStartDate)
    queryParams.set('compareEndDate', filters.compareEndDate)
  }

  // Geo filter
  if (geoSelection) {
    if (geoSelection.type === 'country') queryParams.set('country', geoSelection.name)
    else if (geoSelection.type === 'region') queryParams.set('region', geoSelection.name)
    else if (geoSelection.type === 'city') queryParams.set('city', geoSelection.name)
  }

  const { data: overview, loading: overviewLoading } = useApi<any>(
    ga4Connected
      ? `/api/analytics/clients/${projectId}/overview?${queryParams}`
      : ''
  )

  if (clientError === 'Unauthorized' || clientError === '401') {
    navigate('/login')
    return null
  }

  // Tant que les connexions chargent, on ne montre pas l'état « non configuré » (évite un flash).
  const gaNotConfigured = !connectionsLoading && !ga4Connected
  const loading = clientLoading || connectionsLoading || overviewLoading

  return (
    <div className="text-[#1B2559]">
      {/* Header */}
      <div className="flex items-center justify-between gap-4 mb-6">
        <div className="min-w-0">
          {client ? (
            <h1 className="font-semibold text-[#1B2559] truncate">{client.name} &mdash; Analytics</h1>
          ) : (
            <div className="h-5 w-48 bg-[#F4F5FA] rounded animate-pulse" />
          )}
        </div>
      </div>

      {/* Filters */}
      {!gaNotConfigured && (
        <div className="mb-6 space-y-3">
          <div className="flex items-start gap-3 flex-wrap">
            <div className="flex-1 min-w-0">
              <AnalyticsFilters filters={filters} onChange={setFilters} />
            </div>
            <GeoFilter
              clientId={projectId || ''}
              value={geoSelection}
              onChange={setGeoSelection}
            />
          </div>
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center py-20">
          <div className="w-6 h-6 border-2 border-[#6366F1] border-t-transparent rounded-full animate-spin" />
        </div>
      )}

      {/* GA not configured */}
      {!loading && gaNotConfigured && (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <div className="w-14 h-14 rounded-2xl bg-white border border-[#E2E4ED] flex items-center justify-center mb-4">
            <BarChart2 className="w-6 h-6 text-[#555A73]" strokeWidth={1.25} />
          </div>
          <p className="text-[#1B2559] font-semibold mb-1">Google Analytics non configur&eacute;</p>
          <p className="text-[#8B8FA7] text-sm max-w-xs">
            Connectez Google Analytics dans les param&egrave;tres du projet pour activer le tableau de bord.
          </p>
        </div>
      )}

      {/* Dashboard */}
      {!loading && !gaNotConfigured && overview && (
        <div className="space-y-6">
          {overview.kpis && (
            <AnalyticsKPIs kpis={overview.kpis} />
          )}

          {overview.trend && (
            <TrafficTrendChart
              data={overview.trend}
              previousData={overview.previousTrend}
            />
          )}

          {overview.channels && (
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
              <ChannelDistributionChart data={overview.channels.distribution || []} />
              <ChannelTrendsChart data={overview.channels.trends || { dates: [], channels: [], series: [] }} />
            </div>
          )}

          {overview.journey && (
            <TrafficJourney
              sources={overview.journey.sources || []}
              landingPages={overview.journey.landingPages || []}
              domain={client?.domain || ''}
            />
          )}

          {overview.topPages && (
            <TopPagesTable pages={overview.topPages} />
          )}

          {overview.geo?.countries && (
            <GeoDistribution countries={overview.geo.countries} />
          )}
        </div>
      )}
    </div>
  )
}
